import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { partnerApi } from '../api/partnerApi';

// Async thunks
export const fetchPartnerLeads = createAsyncThunk(
  'partner/fetchLeads',
  async (_, { rejectWithValue }) => {
    try {
      const response = await partnerApi.getLeads();
      return response.leads;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leads');
    }
  }
);

export const acceptLead = createAsyncThunk(
  'partner/acceptLead',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await partnerApi.acceptLead(leadId);
      return { leadId, booking: response.booking };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to accept lead');
    }
  }
);

export const rejectLead = createAsyncThunk(
  'partner/rejectLead',
  async (leadId, { rejectWithValue }) => {
    try {
      await partnerApi.rejectLead(leadId);
      return leadId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject lead');
    }
  }
);

export const toggleAvailability = createAsyncThunk(
  'partner/toggleAvailability',
  async (_, { rejectWithValue }) => {
    try {
      const response = await partnerApi.toggleAvailability();
      return response.available;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update availability');
    }
  }
);

export const fetchPartnerStats = createAsyncThunk(
  'partner/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await partnerApi.getStats();
      return response.stats;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

const initialState = {
  leads: [],
  acceptedBookings: [],
  stats: {
    totalEarnings: 0,
    completedSessions: 0,
    pendingBookings: 0,
    rating: 0,
  },
  isAvailable: true,
  loading: false,
  error: null,
};

const partnerSlice = createSlice({
  name: 'partner',
  initialState,
  reducers: {
    addNewLead: (state, action) => {
      state.leads.unshift(action.payload);
    },
    removeLead: (state, action) => {
      state.leads = state.leads.filter((lead) => lead.id !== action.payload);
    },
    updateLeadStatus: (state, action) => {
      const { leadId, status } = action.payload;
      const lead = state.leads.find((l) => l.id === leadId);
      if (lead) {
        lead.status = status;
      }
    },
    setAvailability: (state, action) => {
      state.isAvailable = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Leads
      .addCase(fetchPartnerLeads.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPartnerLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload;
      })
      .addCase(fetchPartnerLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Accept Lead
      .addCase(acceptLead.fulfilled, (state, action) => {
        const { leadId, booking } = action.payload;
        state.leads = state.leads.filter((lead) => lead.id !== leadId);
        state.acceptedBookings.unshift(booking);
      })
      // Reject Lead
      .addCase(rejectLead.fulfilled, (state, action) => {
        state.leads = state.leads.filter((lead) => lead.id !== action.payload);
      })
      // Toggle Availability
      .addCase(toggleAvailability.fulfilled, (state, action) => {
        state.isAvailable = action.payload;
      })
      // Fetch Stats
      .addCase(fetchPartnerStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { addNewLead, removeLead, updateLeadStatus, setAvailability } = partnerSlice.actions;
export default partnerSlice.reducer;
